/**
 * SelectionToggle — entity-agnostic eye/heart cycle button.
 *
 * Props:
 *   state         — "considering" | "saved" | "disqualified" | undefined
 *   onCycle       — () => void; called when the user clicks the primary cycle
 *                   (undefined → considering → saved → undefined)
 *   onDisqualify  — () => void; optional separate negative action; when omitted
 *                   the disqualify affordance is hidden (used on cards where
 *                   the × is rendered separately)
 *   onUndo        — () => void; optional; shown when state is "disqualified"
 *                   to reset back to undefined
 *   size          — "small" | "medium" (default "medium")
 *   disabled      — bool; when true the button is interactive but no-op
 *                   (used to nudge anonymous users to sign in upstream)
 *
 * Renders a single MUI IconButton whose icon + tooltip + theme reflect the
 * current state. The same component is used on cards, in the modal action
 * bar, and on comparison column headers — anywhere a single-entity toggle
 * is needed.
 */
import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import BlockIcon from '@mui/icons-material/Block';
import { SAGE, WARM } from '../../theme';

const TOOLTIPS = {
  undefined: 'Mark as considering',
  considering: 'Save for later',
  saved: 'Clear',
  disqualified: 'Not for me · undo to clear',
};

const SelectionToggle = ({
  state,
  onCycle,
  onUndo,
  size = 'medium',
  disabled = false,
}) => {
  // Disqualified is a separate negative state — it's shown but not part of
  // the cycle. The "undo" affordance flips it back to undefined.
  if (state === 'disqualified') {
    return (
      <Tooltip title={TOOLTIPS.disqualified}>
        <span>
          <IconButton
            size={size}
            onClick={onUndo}
            disabled={disabled}
            aria-label="Undo disqualify"
            sx={{ color: 'text.disabled' }}
          >
            <BlockIcon fontSize={size === 'small' ? 'small' : 'medium'} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const Icon =
    state === 'saved' ? FavoriteIcon
    : state === 'considering' ? VisibilityIcon
    : VisibilityOutlinedIcon;

  const color =
    state === 'saved' ? WARM.accent
    : state === 'considering' ? SAGE[600]
    : SAGE[400];

  return (
    <Tooltip title={TOOLTIPS[state ?? 'undefined']}>
      <span>
        <IconButton
          size={size}
          onClick={onCycle}
          disabled={disabled}
          aria-label={TOOLTIPS[state ?? 'undefined']}
          sx={{ color }}
        >
          <Icon fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default SelectionToggle;
